import React from "react";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { GlobalContext } from "../../../context/GlobalContext";
import { TranslateContext } from "../../../context/TranslateContext";

/** Official CDN bundle (index.html) sets `window.PaystackPop`; npm is fallback. */
function getPaystackCtor() {
  if (typeof window !== "undefined" && typeof window.PaystackPop === "function") {
    return window.PaystackPop;
  }
  return null;
}

function chargedAsLine(plan, settlement, lang) {
  if (!settlement || !plan?.id) return null;
  const usd = parseFloat(String(plan.price ?? "0")) || 0;
  const local = (usd * (Number(settlement.rate) || 1)).toFixed(2);
  return `${lang?.checkoutChargedAs || "Charged as"} ${settlement.symbol}${local} ${settlement.code}`;
}

function paystackErrDetail(err) {
  if (err == null) return "";
  if (typeof err === "string") return err;
  if (typeof err.message === "string" && err.message.trim()) return err.message.trim();
  try {
    return JSON.stringify(err);
  } catch {
    return "";
  }
}

const PaystackComp = ({ plan, paying, setPaying, gwData, hideChargedAsLine }) => {
  const history = useHistory();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);

  const isLoading = paying === "paystack";

  async function handlePaystack() {
    setPaying("paystack");
    try {
      const res = await hitAxios({
        path: "/api/payment/paystack/create-order",
        admin: false,
        post: true,
        obj: { plan_id: plan.id ?? plan.plan_id },
        showLoading: false,
      });
      if (!res?.data?.success) {
        setPaying("");
        return;
      }

      let d = res.data || {};
      if (d.success && d.data && typeof d.data === "object" && !Array.isArray(d.data)) {
        d = { ...d, ...d.data };
      }

      let PaystackCtor = getPaystackCtor();
      if (!PaystackCtor) {
        try {
          const mod = await import("@paystack/inline-js");
          PaystackCtor = mod?.default ?? mod;
        } catch {
          PaystackCtor = null;
        }
      }
      if (typeof PaystackCtor !== "function") {
        setPaying("");
        showSnack(
          lang?.paystackInlineError || "Payment could not open. Please try again.",
          false,
        );
        return;
      }

      const accessCode = d.access_code ?? d.accessCode;
      const publicKey = d.publicKey ?? d.public_key ?? gwData?.publicKey;
      const email = typeof d.email === "string" ? d.email.trim() : d.email;
      const amountNum = Number(d.amount);
      const amountOk = Number.isFinite(amountNum) && amountNum > 0;

      const onSuccess = (transaction) => {
        setPaying("");
        const ref = transaction?.reference || transaction?.trxref;
        if (!ref) return;
        history.push(`/checkout/thank-you?gateway=paystack&reference=${encodeURIComponent(String(ref))}`);
      };

      const onCancel = () => setPaying("");

      const onError = (err) => {
        setPaying("");
        const detail = paystackErrDetail(err);
        showSnack(
          detail || lang?.paystackInlineError || "Payment could not open. Please try again.",
          false,
        );
      };

      let popup;
      try {
        popup = new PaystackCtor();
      } catch (e) {
        setPaying("");
        showSnack(e?.message || lang?.paystackInlineError || "Payment could not open.", false);
        return;
      }

      if (accessCode) {
        popup.resumeTransaction(String(accessCode), { onSuccess, onCancel, onError });
        return;
      }

      if (!publicKey || !email || !amountOk) {
        setPaying("");
        showSnack(
          lang?.paystackInlineError || "Payment could not open. Please try again.",
          false,
        );
        return;
      }

      popup.newTransaction({
        key: publicKey,
        email,
        amount: amountNum,
        currency: d.currency,
        reference: d.reference,
        metadata: d.metadata,
        onSuccess,
        onCancel,
        onError,
      });
    } catch (e) {
      setPaying("");
      showSnack(
        e?.message || lang?.paystackInlineError || "Payment could not open. Please try again.",
        false,
      );
    }
  }

  return (
    <Stack spacing={0.5}>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        disabled={!!paying}
        onClick={handlePaystack}
        startIcon={
          isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <span style={{ fontSize: "1.1rem" }}>🟩</span>
          )
        }
        sx={{
          justifyContent: "flex-start",
          borderRadius: 2,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.95rem",
          py: 1.2,
        }}
      >
        {isLoading
          ? lang?.paystackOpening || "Opening secure payment…"
          : lang?.payNow || "Pay Now"}
      </Button>
      {gwData?.settlement && !hideChargedAsLine ? (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          {chargedAsLine(plan, gwData.settlement, lang)}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default PaystackComp;
